// React
import { useMemo, useState } from "react";
import { useQuery, gql, useSubscription } from "@apollo/client";

// Components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import { Card, Grid, Select, MenuItem, InputLabel, Icon, IconButton } from "@mui/material";
import { getUproColor } from "utils/colors";
import CircularLoading from "components/Carteles/CircularLoading";
import SinDatos from "components/Carteles/SinDatos";

const REPORTE_RESERVAS = gql`
  query ReporteReservas($fecha: String, $turno: String) {
    ReporteReservas(fecha: $fecha, turno: $turno) {
      idProducto
      productoNombre
      estadoPedido
      totalPedidos
    }
  }
`;

const SUB_NUEVO_PEDIDO = gql`
  subscription NuevoPedido {
    nuevoPedido {
      id
    }
  }
`;

const SUB_PEDIDO_MODIFICADO = gql`
  subscription PedidoModificado {
    pedidoModificado {
      id
    }
  }
`;

const normalizeText = (value = "") =>
  value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z\s]/g, "")
    .toUpperCase();

const estadoFieldMap = {
  "PAGO EN REVISION": "pagoRevision",
  RESERVADO: "reservados",
  "EN PREPARACION": "enPreparacion",
  CERRADO: "entregados",
};

const gridTemplateColumns =
  "minmax(220px, 2fr) repeat(4, minmax(120px, 1fr)) minmax(140px, 1fr)";

const todayISO = () => {
  const now = new Date();
  const timezoneOffset = now.getTimezoneOffset();
  const adjusted = new Date(now.getTime() - timezoneOffset * 60 * 1000);
  return adjusted.toISOString().split("T")[0];
};

const formatToday = () =>
  new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date());

function MonitorPedidos() {
  const [turno, setTurno] = useState("TODOS");
  const fecha = useMemo(() => todayISO(), []);

  const { data, loading, error, refetch } = useQuery(REPORTE_RESERVAS, {
    variables: { fecha, turno },
    fetchPolicy: "cache-and-network",
  });

  useSubscription(SUB_NUEVO_PEDIDO, {
    onData: () => {
      refetch({ fecha, turno });
    },
  });

  useSubscription(SUB_PEDIDO_MODIFICADO, {
    onData: () => {
      refetch({ fecha, turno });
    },
  });

  const handleManualRefresh = () => {
    refetch({ fecha, turno });
  };

  const tableData = useMemo(() => {
    const grouped = new Map();

    (data?.ReporteReservas || []).forEach((item) => {
      const estadoKey = estadoFieldMap[normalizeText(item?.estadoPedido)];
      if (!estadoKey) return;

      const productId = item?.idProducto ?? item?.productoNombre ?? "sin-producto";
      if (!grouped.has(productId)) {
        grouped.set(productId, {
          id: productId,
          producto: item?.productoNombre || "Producto sin nombre",
          pagoRevision: 0,
          reservados: 0,
          enPreparacion: 0,
          entregados: 0,
        });
      }

      const current = grouped.get(productId);
      current[estadoKey] += item?.totalPedidos || 0;
    });

    return Array.from(grouped.values())
      .map((row) => ({
        ...row,
        total: row.pagoRevision + row.reservados + row.enPreparacion + row.entregados,
      }))
      .sort((a, b) => {
        if (b.total !== a.total) return b.total - a.total;
        return a.producto.localeCompare(b.producto);
      });
  }, [data]);

  const totals = useMemo(
    () =>
      tableData.reduce(
        (acc, row) => ({
          pagoRevision: acc.pagoRevision + row.pagoRevision,
          reservados: acc.reservados + row.reservados,
          enPreparacion: acc.enPreparacion + row.enPreparacion,
          entregados: acc.entregados + row.entregados,
          total: acc.total + row.total,
        }),
        { pagoRevision: 0, reservados: 0, enPreparacion: 0, entregados: 0, total: 0 }
      ),
    [tableData]
  );

  const columns = [
    { key: "producto", label: "Producto", align: "left" },
    { key: "pagoRevision", label: "Pago revisión", align: "center" },
    { key: "reservados", label: "Reservados", align: "center" },
    { key: "enPreparacion", label: "En preparación", align: "center" },
    { key: "entregados", label: "Entregados", align: "center" },
    { key: "total", label: "Totales", align: "center" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Card sx={{ padding: 3, borderRadius: 3 }}>
          <SoftBox display="flex" justifyContent="space-between" flexWrap="wrap" gap={1} alignItems="center">
            <SoftTypography variant="h5" fontWeight="bold">
              Monitor de pedidos
            </SoftTypography>
            <SoftBox display="flex" alignItems="center" gap={1}>
              <SoftTypography variant="button" color="text" sx={{ opacity: 0.8 }}>
                Fecha actual: {formatToday()}
              </SoftTypography>
              <IconButton
                onClick={handleManualRefresh}
                disabled={loading}
                size="small"
                sx={{ color: getUproColor("uproBlue") }}
                aria-label="Actualizar datos"
              >
                <Icon fontSize="small">refresh</Icon>
              </IconButton>
            </SoftBox>
          </SoftBox>

          <SoftBox mt={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <InputLabel>
                  <SoftTypography variant="caption" color="text">
                    Turno
                  </SoftTypography>
                </InputLabel>
                <Select value={turno} onChange={(e) => setTurno(e.target.value)} fullWidth>
                  <MenuItem value="TODOS">
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon sx={{ color: getUproColor("uproPink"), fontSize: "1.1rem" }}>
                        all_inclusive
                      </Icon>
                      Todos los turnos
                    </SoftBox>
                  </MenuItem>
                  <MenuItem value="MEDIODIA">
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon sx={{ color: getUproColor("uproYellow"), fontSize: "1.1rem" }}>
                        light_mode
                      </Icon>
                      Mediodía
                    </SoftBox>
                  </MenuItem>
                  <MenuItem value="NOCHE">
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon sx={{ color: getUproColor("uproBlue"), fontSize: "1.1rem" }}>
                        dark_mode
                      </Icon>
                      Noche
                    </SoftBox>
                  </MenuItem>
                </Select>
              </Grid>
            </Grid>
          </SoftBox>

          <SoftBox mt={4}>
            {loading ? (
              <CircularLoading />
            ) : error ? (
              <SoftTypography variant="caption" color="error">
                Ocurrió un error al buscar los pedidos.
              </SoftTypography>
            ) : tableData.length === 0 ? (
              <SinDatos />
            ) : (
              <SoftBox>
                <SoftBox
                  display="grid"
                  gridTemplateColumns={gridTemplateColumns}
                  alignItems="center"
                  px={2}
                  py={1.5}
                  sx={{
                    background: ({ palette }) => palette.info.main,
                    borderRadius: "16px 16px 0 0",
                  }}
                >
                  {columns.map((column) => (
                    <SoftTypography
                      key={column.key}
                      variant="caption"
                      color="white"
                      fontWeight="bold"
                      textAlign={column.align}
                      sx={{ letterSpacing: 0.5 }}
                    >
                      {column.label}
                    </SoftTypography>
                  ))}
                </SoftBox>

                {tableData.map((row, index) => (
                  <SoftBox
                    key={row.id}
                    display="grid"
                    gridTemplateColumns={gridTemplateColumns}
                    alignItems="center"
                    px={2}
                    py={1.5}
                    sx={{
                      backgroundColor: ({ palette }) =>
                        index % 2 === 0 ? palette.background.default : palette.background.paper,
                      borderBottom: ({ palette }) => `1px solid ${palette.divider}`,
                    }}
                  >
                    <SoftTypography variant="button" color="text" fontWeight="medium">
                      {row.producto}
                    </SoftTypography>
                    <SoftTypography textAlign="center" variant="body2" fontWeight="medium" color="text">
                      {row.pagoRevision}
                    </SoftTypography>
                    <SoftTypography textAlign="center" variant="body2" fontWeight="medium" color="text">
                      {row.reservados}
                    </SoftTypography>
                    <SoftTypography textAlign="center" variant="body2" fontWeight="medium" color="text">
                      {row.enPreparacion}
                    </SoftTypography>
                    <SoftTypography textAlign="center" variant="body2" fontWeight="medium" color="text">
                      {row.entregados}
                    </SoftTypography>
                    <SoftTypography textAlign="center" variant="body2" fontWeight="bold" color="text">
                      {row.total}
                    </SoftTypography>
                  </SoftBox>
                ))}

                <SoftBox
                  display="grid"
                  gridTemplateColumns={gridTemplateColumns}
                  alignItems="center"
                  px={2}
                  py={1.5}
                  sx={{
                    background: ({ palette }) => palette.info.light,
                    borderRadius: "0 0 16px 16px",
                  }}
                >
                  <SoftTypography variant="button" fontWeight="bold" color="info.dark">
                    Totales
                  </SoftTypography>
                  <SoftTypography textAlign="center" variant="body2" fontWeight="bold" color="info.dark">
                    {totals.pagoRevision}
                  </SoftTypography>
                  <SoftTypography textAlign="center" variant="body2" fontWeight="bold" color="info.dark">
                    {totals.reservados}
                  </SoftTypography>
                  <SoftTypography textAlign="center" variant="body2" fontWeight="bold" color="info.dark">
                    {totals.enPreparacion}
                  </SoftTypography>
                  <SoftTypography textAlign="center" variant="body2" fontWeight="bold" color="info.dark">
                    {totals.entregados}
                  </SoftTypography>
                  <SoftTypography textAlign="center" variant="body2" fontWeight="bold" color="info.dark">
                    {totals.total}
                  </SoftTypography>
                </SoftBox>

                <SoftBox mt={3} display="flex" justifyContent="flex-start">
                  <SoftTypography variant="caption" pl={1} fontWeight="bold" color="error">
                    (*) Tener en cuenta que los totales pueden diferir de los mostrados en otros reportes dado que no se están considerando todos los estados posibles de un pedido sino solo los más relevantes para la operación diaria.
                  </SoftTypography>
                </SoftBox>

                {/* <SoftBox mt={3} display="flex" justifyContent="flex-end">
                  <SoftBox
                    px={3}
                    py={2}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: ({ palette }) => palette.success.main,
                    }}
                  >
                    <SoftTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                      Total general
                    </SoftTypography>
                    <SoftTypography variant="h4" color="white" fontWeight="bold">
                      {totals.total}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox> */}
              </SoftBox>
            )}
          </SoftBox>
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default MonitorPedidos;

